import { AppError } from '@/lib/error';

export type InviteInfo = { status: 'pending'; email: string } | { status: 'accepted' | 'expired' };

export type UserPreferences = {
  modelVariant?: string;
};

/**
 * User information extracted from the token.
 */
export type AuthUser = {
  id: string;
  email?: string;
  name?: string;
  avatar?: string;
  role?: string;
  roles?: string[];
  permissions?: string[];
  metadata?: Record<string, unknown>;
  preferences: UserPreferences;
};

/**
 * Token information managed by the auth adapter.
 */
export type AuthToken = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt?: Date; // Unix timestamp in milliseconds
  tokenType: 'Bearer' | string;
};

/**
 * Authentication state representing the current session status.
 */
export type AuthState = {
  user: AuthUser | null;
  token: AuthToken | null;
  status: AuthStatus;
  error: AppError | null;
};

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

/**
 * Credentials for adapters that support email/password authentication.
 */
export type CredentialsInput = {
  email: string;
  password: string;
};

export type VerifyPasswordResetInput =
  | {
      email: string;
      code: string;
      userId?: never;
    }
  | {
      userId: string;
      code: string;
      email?: never;
    };

/**
 * Flow type determines the authentication UI behavior.
 * - 'redirect': User is redirected to external provider (OIDC)
 * - 'credentials': User enters credentials in the app (Firebase, REST)
 */
export type AuthFlowType = 'redirect' | 'credentials';

/**
 * Capabilities exposed by the auth adapter.
 * Used by UI components to conditionally render features.
 */
export type AuthCapabilities = {
  canSignInWithCredentials: boolean;
  canSignUp: boolean;
  canResetPassword: boolean;
  canChangePassword: boolean;
  canAcceptInvite: boolean;
};

/**
 * Auth adapter base class that all authentication providers must implement.
 */
export abstract class AuthAdapter {
  /** Unique identifier for this adapter */
  abstract readonly name: string;

  /** The type of authentication flow this adapter uses */
  abstract readonly flowType: AuthFlowType;

  /** Capabilities supported by this adapter */
  abstract readonly capabilities: AuthCapabilities;

  /**
   * Initialize the adapter. Called once when the AuthProvider mounts.
   * Restore any persisted session and set up event listeners.
   */
  abstract initialize(): Promise<void>;

  /**
   * Get the current authentication state.
   */
  abstract getAuthState(): AuthState;

  /**
   * Get a valid access token for API calls, handling token refresh internally if needed.
   * Returns null if the user is not authenticated.
   */
  abstract getAccessToken(): Promise<string | null>;

  /**
   * Sign out the current user and clear all session data.
   */
  abstract signOut(): Promise<void>;

  /**
   * Subscribe to authentication state changes.
   * Returns an unsubscribe function.
   */
  abstract onAuthStateChange(callback: (state: AuthState) => void): () => void;

  /**
   * Initiate sign-in based on the provider flow type.
   */
  async signIn(credentials?: CredentialsInput) {
    if (this.flowType === 'redirect') {
      await this.signInWithRedirect?.();
    } else if (credentials) {
      await this.signInWithCredentials?.(credentials);
    } else {
      throw new AppError('internalError', 'Credentials required for this authentication method');
    }
  }

  /**
   * Initiate sign-in by redirecting to the external provider.
   * Only available when flowType is 'redirect'.
   */
  protected abstract signInWithRedirect(): Promise<void>;

  /**
   * Sign in with email and password.
   * Only available when flowType is 'credentials'.
   */
  protected abstract signInWithCredentials(credentials: CredentialsInput): Promise<AuthState>;

  /**
   * Create a new account with email and password.
   * Only available when capabilities.canSignUp is true.
   */
  abstract signUp(credentials: CredentialsInput & { name?: string }): Promise<AuthState>;

  /**
   * Send a password reset email.
   * Only available when capabilities.canResetPassword is true.
   */
  abstract resetPassword(email: string): Promise<void>;

  /**
   * Verify the OTP code sent via email
   * Only available when capabilities.canResetPassword is true.
   */
  abstract verifyResetPasswordCode(input: VerifyPasswordResetInput): Promise<string>;

  /**
   * Reset the password using userId and OTP code.
   * Only available when capabilities.canResetPassword is true.
   */
  abstract completePasswordReset(email: string, code: string, newPassword: string): Promise<void>;

  /**
   * Change the current user's password.
   * Only available when capabilities.canChangePassword is true.
   */
  abstract changePassword(oldPassword: string, newPassword: string): Promise<void>;

  /**
   * Check the invite status for a given user ID.
   */
  abstract checkInvite(userId: string): Promise<InviteInfo>;

  /**
   * Complete the registration flow for an invited user (set password and sign in).
   * Only available when capabilities.canAcceptInvite is true.
   */
  abstract acceptInvite(userId: string, password: string, email: string): Promise<void>;

  /**
   * Reset the error message form the adapter
   */
  abstract resetErrors(): void;

  /**
   * Persist partial preference updates for the current user.
   */
  abstract updatePreferences(prefs: Partial<UserPreferences>): Promise<void>;
}
